// ════════════════════════════════════════════════════════════
// OfertaEditor — ofertas con líneas del catálogo o escritas a mano
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField,
  Autocomplete, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  IconButton, Select, MenuItem, Typography, Chip, Alert, Grid
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getRecambiosDivision, crearOferta, actualizarOferta, getLineasOferta } from '../api';

const DIVISIONES = ['SANY Cranes', 'SANY Robotics', 'SANY Marine', 'TII SCHEUERLE', 'General'];
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
const r2 = (n: number) => Math.round(n * 100) / 100;

export default function OfertaEditor({ open, onClose, onCreada, clientes, proveedores, oferta }: any) {
  const [cliente, setCliente] = useState<any>(null);
  const [descuento, setDescuento] = useState<number>(0);
  const [condiciones, setCondiciones] = useState('Transferencia 30 días');
  const [observaciones, setObservaciones] = useState('');
  const [lineas, setLineas] = useState<any[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState<any[]>([]);
  const [guardando, setGuardando] = useState(false);

  // Al abrir: formulario limpio, o cargado con la oferta que se va a editar
  useEffect(() => {
    if (!open) return;
    setBusqueda(''); setResultados([]);
    if (oferta) {
      setCliente((clientes || []).find((c: any) => c.id === oferta.cliente_id)
        || (clientes || []).find((c: any) => c.nombre === oferta.cliente) || null);
      setDescuento(oferta.descuento || 0);
      setCondiciones(oferta.condiciones_pago || 'Transferencia 30 días');
      setObservaciones(oferta.observaciones || '');
      setLineas([]);
      getLineasOferta(oferta.id).then((ls: any[]) => setLineas(ls.map((l: any) => ({
        recambio_id: l.recambio_id, es_manual: !!l.es_manual || !l.recambio_id,
        referencia: l.referencia || '', descripcion: l.descripcion || '',
        cantidad: l.cantidad, precio_unitario: l.precio_unitario,
        precio_coste: l.precio_coste || 0, division: l.division || 'General',
        proveedor_id: l.proveedor_id || null,
      })))).catch(() => setLineas([]));
    } else {
      setCliente(null); setDescuento(0); setCondiciones('Transferencia 30 días');
      setObservaciones(''); setLineas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, oferta]);

  // Búsqueda en el catálogo
  useEffect(() => {
    if (busqueda.trim().length < 2) { setResultados([]); return; }
    const t = setTimeout(() => {
      getRecambiosDivision(busqueda.trim(), 1, '')
        .then((d: any) => setResultados(d.items || []))
        .catch(() => setResultados([]));
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const añadirCatalogo = (r: any) => {
    if (!r) return;
    setLineas([...lineas, {
      recambio_id: r.id, es_manual: false, referencia: r.referencia, descripcion: r.nombre,
      cantidad: 1, precio_unitario: r.precio_venta || 0, precio_coste: r.precio_compra || 0,
      division: r.division || 'General', proveedor_id: null,
      stock_disponible: r.stock_disponible ?? r.stock_actual ?? 0,
    }]);
    setBusqueda('');
    setResultados([]);
  };

  const añadirManual = () => setLineas([...lineas, {
    recambio_id: null, es_manual: true, referencia: '', descripcion: '', cantidad: 1,
    precio_unitario: 0, precio_coste: 0, division: 'General', proveedor_id: null,
  }]);

  const setLinea = (i: number, campo: string, valor: any) => {
    const nuevas = [...lineas];
    nuevas[i] = { ...nuevas[i], [campo]: valor };
    setLineas(nuevas);
  };

  const bruto = lineas.reduce((s, l) =>
    s + (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0), 0);
  const base = r2(bruto * (1 - (descuento || 0) / 100));
  const iva = r2(base * 0.21);
  const total = r2(base + iva);

  const crear = async () => {
    if (!cliente) { alert('Selecciona el cliente'); return; }
    const validas = lineas.filter(l => (l.descripcion || '').trim());
    if (!validas.length) { alert('Añade al menos una línea con descripción'); return; }
    const manualesSinProv = validas.filter(l => l.es_manual && !l.proveedor_id).length;
    if (manualesSinProv && !window.confirm(
      `Hay ${manualesSinProv} nueva(s) referencia(s) sin proveedor asignado.\n\n` +
      `Al aceptar la oferta se pedirán a "Proveedor por asignar" y podrás elegirlo luego en Compras. ¿Continuar?`)) return;

    setGuardando(true);
    try {
      const datos = {
        cliente: cliente.nombre, cliente_id: cliente.id, descuento,
        base_imponible: base, cuota_iva: iva, total,
        condiciones_pago: condiciones, observaciones,
        lineas: validas.map(l => {
          const cant = parseFloat(l.cantidad) || 0;
          const precio = parseFloat(l.precio_unitario) || 0;
          return {
            recambio_id: l.recambio_id, es_manual: l.es_manual,
            referencia: l.referencia || '', descripcion: l.descripcion.trim(),
            cantidad: cant, precio_unitario: precio, total: r2(cant * precio),
            precio_coste: parseFloat(l.precio_coste) || 0,
            proveedor_id: l.proveedor_id || null, division: l.division,
          };
        }),
      };
      if (oferta) {
        await actualizarOferta(oferta.id, datos);
        onCreada({ numero: oferta.numero, editada: true });
      } else {
        const r = await crearOferta(datos);
        onCreada(r);
      }
      onClose();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al crear la oferta');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="lg" fullWidth
      onClose={(e, rz) => { if (rz !== 'backdropClick' && rz !== 'escapeKeyDown') onClose(); }}>
      <DialogTitle>{oferta ? `✏️ Editar oferta ${oferta.numero}` : '📄 Nueva oferta de recambios'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
          <Grid item xs={12} md={5}>
            <Autocomplete options={clientes || []} value={cliente}
              getOptionLabel={(c: any) => c.nombre || ''}
              onChange={(_, v) => setCliente(v)}
              renderInput={(p) => <TextField {...p} size="small" label="Cliente *" />} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="number" label="Descuento %"
              value={descuento} onChange={(e) => setDescuento(parseFloat(e.target.value) || 0)} />
          </Grid>
          <Grid item xs={6} md={5}>
            <TextField fullWidth size="small" label="Condiciones de pago"
              value={condiciones} onChange={(e) => setCondiciones(e.target.value)} />
          </Grid>
        </Grid>

        {/* Añadir líneas */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete sx={{ flex: 1, minWidth: 280 }}
            options={resultados} inputValue={busqueda} value={null}
            onInputChange={(_, v, reason) => { if (reason !== 'reset') setBusqueda(v); }}
            onChange={(_, v) => añadirCatalogo(v)}
            getOptionLabel={(r: any) => `${r.referencia} — ${r.nombre}`}
            filterOptions={(x) => x}
            noOptionsText={busqueda.length < 2 ? 'Escribe al menos 2 letras' : 'Sin resultados'}
            renderOption={(props, r: any) => (
              <li {...props} key={r.id}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.referencia} — {r.nombre}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.division || 'General'} · Disponible: {r.stock_disponible ?? r.stock_actual} · {eur(r.precio_venta)}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(p) => <TextField {...p} size="small" label="Buscar en el catálogo (referencia o nombre)" />} />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={añadirManual}>
            Nueva referencia
          </Button>
        </Box>

        {lineas.length > 0 && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1050 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Origen</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Referencia</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>División</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 80 }}>Cant.</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>Precio venta</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>Coste</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 180 }}>Proveedor (si hay que pedir)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {lineas.map((l, i) => {
                  const cant = parseFloat(l.cantidad) || 0;
                  const falta = l.es_manual ? cant
                    : (l.stock_disponible === undefined ? 0 : Math.max(0, cant - l.stock_disponible));
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <Chip size="small" label={l.es_manual ? 'Pedido a proveedor' : 'Catálogo'}
                          color={l.es_manual ? 'warning' : 'default'} variant="outlined" />
                        {!l.es_manual && falta > 0 && (
                          <Typography variant="caption" display="block" color="warning.main">
                            Se pedirán {falta}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {l.es_manual
                          ? <TextField variant="standard" value={l.referencia}
                              onChange={(e) => setLinea(i, 'referencia', e.target.value)} />
                          : l.referencia}
                      </TableCell>
                      <TableCell>
                        {l.es_manual
                          ? <TextField variant="standard" fullWidth placeholder="Descripción *" value={l.descripcion}
                              onChange={(e) => setLinea(i, 'descripcion', e.target.value)} />
                          : l.descripcion}
                      </TableCell>
                      <TableCell>
                        {l.es_manual
                          ? <Select variant="standard" value={l.division}
                              onChange={(e) => setLinea(i, 'division', e.target.value)}>
                              {DIVISIONES.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                            </Select>
                          : l.division}
                      </TableCell>
                      <TableCell>
                        <TextField variant="standard" type="number" value={l.cantidad}
                          onChange={(e) => setLinea(i, 'cantidad', e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <TextField variant="standard" type="number" value={l.precio_unitario}
                          onChange={(e) => setLinea(i, 'precio_unitario', e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <TextField variant="standard" type="number" value={l.precio_coste}
                          onChange={(e) => setLinea(i, 'precio_coste', e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <Select variant="standard" fullWidth displayEmpty value={l.proveedor_id || ''}
                          onChange={(e) => setLinea(i, 'proveedor_id', e.target.value || null)}>
                          <MenuItem value=""><em>Sin asignar</em></MenuItem>
                          {(proveedores || []).map((p: any) =>
                            <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
                        </Select>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {eur(cant * (parseFloat(l.precio_unitario) || 0))}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error"
                          onClick={() => setLineas(lineas.filter((_, j) => j !== i))}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={7}>
            <TextField fullWidth multiline rows={2} size="small" label="Observaciones"
              value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Base imponible</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(base)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">IVA 21%</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(iva)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography sx={{ fontWeight: 700 }}>TOTAL</Typography>
                <Typography sx={{ fontWeight: 800 }}>{eur(total)}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={crear} disabled={guardando} sx={{ bgcolor: '#4f46e5' }}>
          {guardando ? 'Guardando...' : (oferta ? 'Guardar cambios' : 'Crear oferta')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}